import React, { useState } from 'react'
import { Stack } from '@hub/stack/src'
import { Text } from '@hub/text/src'
import { Box } from '@hub/box/src'
import { CoreContainer } from '@hub/core-container/src'
import { useToken } from '@hub/design-system-base/src'
import { CallToAction } from '@scentregroup/ui'
import { MovieHeader } from '@scentregroup/shared/hub-components/movie-header'
import { StorefrontUtility } from '@scentregroup/shared/hub-components/storefront-utility'
import MovieProviderBanner from '@scentregroup/shared/components/movie-feature/movie-provider-banner'
import Tabs from '@scentregroup/shared/components/tabs-v2'
import NowShowing, {
  MovieListItem,
} from '@scentregroup/shared/components/now-showing'
import { MOVIE_DAYS_IN_ADVANCE } from '@scentregroup/shared/constants'
import { getWeekAhead } from '@scentregroup/shared/helpers/date-helpers'
import { mapStoreTradingHourDayToTradingSchedule } from '@scentregroup/shared/helpers/get-trading-schedule'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { SGPageTemplateWithAdZones } from '@scentregroup/shared/types/page-templates'
import { SimpleStore } from '../../../lib/get-content-detail/cinema'
import { Template } from '@scentregroup/shared/components/page-templates/template'

export interface Movie {
  image: string
  genre: string
  title: string
  classification: string
  backgroundImage: string
  duration?: number
  synopsis: string
  trailerUrl: string
  trailerCaptions?: string
  flicksLink: string
  sessions: MovieSession[]
  typename: string
  slug: string
}

export interface MovieSession {
  shortName: string
  name: string
  date: string
  sessions: SessionSession[]
}

export interface SessionSession {
  time: string
  link: string
}

interface IMovieContentDetailProps {
  centre: React.ComponentProps<typeof StorefrontUtility>['centre']
  content: Movie
  movies: MovieListItem[]
  cinema: SimpleStore
  template: SGPageTemplateWithAdZones | null
  storeTradingHours?: TradingHourDay[]
  centreTradingHours?: TradingHourDay[]
}

export const MovieContentDetail: React.FC<
  React.PropsWithChildren<IMovieContentDetailProps>
> = ({
  centre,
  content: movie,
  cinema,
  movies,
  template,
  storeTradingHours,
  centreTradingHours,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const sessionTimeButtonMinWidth = useToken('space', 'spacing-12')

  return (
    <Stack gap={['spacing-3xl', null, 'spacing-4xl']}>
      <CoreContainer isMobileFullWidth>
        <MovieHeader movie={movie} />

        <Box px={['spacing-mobile-margin', 0]} pt={['spacing-lg']}>
          <Tabs
            justifyHeaderStart
            activeTab={activeTab}
            tabHeadings={movie.sessions.map(s => s.shortName)}
            onTabHeadingClick={setActiveTab}
          >
            {movie.sessions.map(date => (
              <section key={date.shortName}>
                <Box my="spacing-md">
                  <Text fontSize="font-md">{date.name}</Text>
                </Box>
                <Stack
                  direction="row"
                  sx={{
                    gap: 8,
                    flexFlow: 'wrap',
                  }}
                >
                  {date.sessions.map(session => (
                    <Box
                      key={session.time}
                      sx={{
                        minWidth: sessionTimeButtonMinWidth,
                      }}
                    >
                      <CallToAction
                        inverted
                        fullWidth
                        key={session.time}
                        element={'a'}
                        href={session.link}
                        target="_blank"
                      >
                        {session.time.toLowerCase()}
                      </CallToAction>
                    </Box>
                  ))}
                </Stack>
              </section>
            ))}
          </Tabs>
          <MovieProviderBanner flicksLink={movie.flicksLink} />
        </Box>
      </CoreContainer>
      <Box px={['spacing-mobile-margin', 0]}>
        <StorefrontUtility
          disablePadding
          centre={centre}
          store={cinema}
          centreTradingSchedule={mapStoreTradingHourDayToTradingSchedule(
            centreTradingHours || []
          )}
          storeTradingSchedule={mapStoreTradingHourDayToTradingSchedule(
            storeTradingHours || []
          )}
        />
      </Box>
      <NowShowing
        title="Now showing"
        entries={movies}
        centreSlug={centre.slug}
        tabHeadings={getWeekAhead(centre.timeZone, MOVIE_DAYS_IN_ADVANCE)}
      />
      {template && <Template template={template} />}
    </Stack>
  )
}
